/**
 * Seat: move parts along an axis of their parent until they rest on what is in
 * their way, in order, and say what each now rests on. Reads the scene at Base,
 * and in a deck only counts parts its pages build no later than the part; a part
 * Base hides is seated on the last page that shows it.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import { holdingDeck } from '../../editor/pages/holdingDeck';
import { topLevelParent } from '../../editor/authoring/documentRoot';
import { walkDeck } from './deck';
import { fitPart, sizeOf, studGrid, type FitPart, type StudGrid } from './parts';
import { partsIn, partsShown } from './scene';
import { SEAT_DIRECTIONS, sweepContact, type SeatDirection } from './sweep';
import type { Vec3 } from './solids';

export interface SeatRequest {
    readonly object: SceneObject;
    /** Travel in the object's parent frame; `-y` (down) when unset. */
    readonly dir?: SeatDirection;
    /** Only these objects, and what is inside them, count as things to rest on. */
    readonly on?: readonly SceneObject[];
    /** These never count. */
    readonly ignore?: readonly SceneObject[];
}

export interface SeatFact {
    readonly object: SceneObject;
    readonly dir: SeatDirection;
    /** Not seated, and why. */
    readonly error?: string;
    /** Only measured (a part the build placed without seat): nothing moved. */
    readonly checked?: boolean;
    /** Distance along `dir`, in the parent frame; negative when it backed out. */
    readonly moved: number;
    /** Under the tolerance: reported as placed. */
    readonly asPlaced: boolean;
    readonly nothing: boolean;
    /** Its leading face along the travel, in the parent frame, where it ended up. */
    readonly face: number;
    /** What it touches where it stopped; `tops` when that is only stud tops it did not engage. */
    readonly contacts: readonly { readonly object: SceneObject; readonly share: number; readonly knobs: number; readonly tops: boolean }[];
    readonly startedInside: readonly SceneObject[];
    /** Of those, the ones narrower than it across the travel: a pin it may be meant to sit over. */
    readonly pins: readonly SceneObject[];
    /** Of those, the ones that run through it across the travel, no thicker than it along it: a pin or axle fitted sideways. */
    readonly sideways: readonly SceneObject[];
    /** Measured with a gap: what it stops on that passes through it (a rim in a tyre, a pin in a hole). */
    readonly through: readonly SceneObject[];
    /** Measured with a gap: what it stops on that runs across the travel, long and narrow (a pin or an axle, maybe meant for its hole). */
    readonly across: readonly SceneObject[];
    /** Its own extent along the travel: a back-out larger than this started on the wrong side. */
    readonly height: number;
    /** Measured with a gap under it: what it touches now on any side, `later` when its deck shows that part on a later page. */
    readonly touching?: readonly { readonly object: SceneObject; readonly later: boolean }[];
    /** Measured with a gap under it and touching nothing: the nearest part beside or above it, how far, and which way. */
    readonly beside?: { readonly object: SceneObject; readonly gap: number; readonly dir: SeatDirection };
    /** The deck page (0-based) it was seated on, when Base hides it. */
    readonly page?: number;
    /** The model's studs, when it is built from studded bricks: the wording speaks of studs only then. */
    readonly grid: StudGrid | null;
}

const AXIS = { x: 0, y: 1, z: 2 } as const;
const vectorOf = (dir: SeatDirection): Vec3 => {
    const v: Vec3 = [0, 0, 0];
    v[AXIS[dir[1] as 'x' | 'y' | 'z']] = dir[0] === '-' ? -1 : 1;
    return v;
};

/** The deck an object sits in, if any. */
function deckOf(object: SceneObject): SceneObject | null {
    for (let n: SceneObject | null = object.getParent(); n; n = n.getParent()) if (n.getBehavior(Pages)) return n;
    return null;
}

/** What a part can rest on by default: in a deck, the deck child it is in (the model); else its top-level object. */
function scopeOf(object: SceneObject): SceneObject {
    const deck = deckOf(object), top = topLevelParent(object.traceScene);
    let n = object;
    for (let p = n.getParent(); p && p !== deck && p !== top && p !== object.traceScene.getSceneRoot(); p = p.getParent()) n = p;
    return n;
}

const within = (object: SceneObject, roots: ReadonlySet<SceneObject>): boolean => {
    for (let n: SceneObject | null = object; n; n = n.getParent()) if (roots.has(n)) return true;
    return false;
};

/** Where the decks show each part: its first and last page (0-based), and the cues, shown only at rest on a page. */
interface Shown {
    readonly first: Map<SceneObject, number>;
    readonly last: Map<SceneObject, number>;
    readonly cues: Set<SceneObject>;
}

async function shownOn(decks: readonly SceneObject[]): Promise<Shown> {
    const shown: Shown = { first: new Map(), last: new Map(), cues: new Set() };
    for (const deck of decks) {
        const { pages, moving } = await walkDeck(deck, [deck], () => partsShown([deck]));
        pages.forEach((parts, k) => {
            for (const part of parts) {
                if (!shown.first.has(part)) shown.first.set(part, k);
                shown.last.set(part, k);
                if (!moving.has(part)) shown.cues.add(part);
            }
        });
    }
    return shown;
}

/** The last page (0-based) its deck shows anything under `object` on, or null. */
function lastPage(object: SceneObject, shown: Shown): number | null {
    let page: number | null = null;
    for (const [part, k] of shown.last) if ((page === null || k > page) && within(part, new Set([object]))) page = k;
    return page;
}

/** Run with every group under and above `roots` at Base (no editor pick showing a state), then pick again. */
function atBase<T>(roots: readonly SceneObject[], run: () => T): T {
    const objects = new Set<SceneObject>();
    for (const root of roots) {
        for (let n: SceneObject | null = root; n; n = n.getParent()) objects.add(n);
        for (const d of root.getDescendants()) objects.add(d);
    }
    const picks = [...objects].flatMap(o => {
        const props = o.getBehavior(Properties);
        return props ? [{ props, pick: props.getEditorState() }] : [];
    });
    try {
        for (const { props } of picks) props.setEditorState(null, true);
        return run();
    } finally {
        for (const { props, pick } of picks) {
            try { if (props.getEditorState() !== pick) props.setEditorState(pick, true); } catch { /* removed */ }
        }
    }
}

/** One part made of everything that shows under `object`, in its parent's frame. */
function moverPart(object: SceneObject, frame: SceneObject): FitPart | null {
    const parts = [...partsIn(frame, [object]).values()].map(p => p.part);
    if (parts.length <= 1) return parts[0] ?? null;
    return fitPart(parts.flatMap(p => p.solids), parts[0].up);
}

/** Parts to measure without moving: those under `among` that `created` holds, beside the parts seated. */
export interface SeatCheck {
    readonly among: readonly SceneObject[];
    readonly created: ReadonlySet<SceneObject>;
}

/**
 * Seat each request in order, moving it in its parent along `dir` (see `sweepContact`), except one that starts
 * inside a part that holds it (`Sweep.held`: a tube, a bore, a pocket, a hub round a rod), which is left in place;
 * then measure the parts of `check` that share a seated part's scope along their parent's -Y without moving them.
 * Parts are read at Base; one Base hides is seated on the last page that shows it. A part not yet seated, a part its
 * deck shows on a later page, a cue and what `ignore` names are not in the way. Moves are written to the objects'
 * transforms; the caller records them for undo.
 */
export async function seatParts(requests: readonly SeatRequest[], check?: SeatCheck): Promise<SeatFact[]> {
    if (!requests.length) return [];
    const decks = [...new Set(requests.map(r => deckOf(r.object)).filter((d): d is SceneObject => !!d))];
    const shown = await shownOn(decks);
    const scopes = new Set(requests.map(r => scopeOf(r.object)));
    const roots = [...scopes, ...decks];
    // Read once at Base: which requests Base hides (a step its deck shows only on some pages), and the model's studs.
    const { hidden, grid } = atBase(roots, () => ({
        hidden: new Set(requests.filter(r => deckOf(r.object) && !partsShown([r.object]).size).map(r => r.object)),
        grid: studGrid([...new Set([...scopes].flatMap(scope => [...partsIn(scope, [scope]).entries()].filter(([o]) => !shown.cues.has(o)).map(([, pose]) => pose.part)))]),
    }));
    const pending = new Set(requests.map(r => r.object));
    const facts: SeatFact[] = [];
    for (const request of requests) {
        pending.delete(request.object);
        // A part Base hides is seated on the last page that shows it, which poses the deck as that page does;
        // the others at Base, where it doesn't show.
        const deck = deckOf(request.object), page = hidden.has(request.object) ? lastPage(request.object, shown) : null;
        if (page === null) facts.push(atBase(roots, () => seatOne(request, false, pending, shown, grid)));
        else facts.push({ ...await holdingDeck(deck!, show => { show(page); return Promise.resolve(seatOne(request, false, pending, shown, grid)); }), page });
    }
    const checks = !check ? [] : atBase(roots, () => {
        const out: SeatFact[] = [];
        const seated = new Set(requests.map(r => r.object));
        const holding = new Set([...seated].flatMap(o => { const up: SceneObject[] = []; for (let n = o.getParent(); n; n = n.getParent()) up.push(n); return up; }));
        for (const part of partsShown(check.among)) {
            if (!check.created.has(part) || within(part, seated) || holding.has(part) || !scopes.has(scopeOf(part)) || shown.cues.has(part)) continue;
            // A part it can't measure (a bound position) was never asked for: nothing to say.
            const fact = seatOne({ object: part }, true, pending, shown, grid);
            if (!fact.error) out.push(fact);
        }
        return out;
    });
    return [...facts, ...checks];
}

function seatOne(request: SeatRequest, checked: boolean, pending: ReadonlySet<SceneObject>, shown: Shown, grid: StudGrid | null): SeatFact {
    const { object } = request;
    const dir = request.dir ?? '-y';
    const { first, cues } = shown;
    const blank = { object, dir, checked, moved: 0, asPlaced: false, nothing: false, face: 0, contacts: [], startedInside: [], pins: [], sideways: [], through: [], across: [], height: 0, grid };
    const fail = (error: string): SeatFact => ({ ...blank, error });
    const frame = object.getParent();
    if (!frame) return fail('it has no parent');
    if (object.getBehavior(Properties)?.getData().transform?.position) return fail('its position is bound; seat what is inside it');
    const a = AXIS[dir[1] as 'x' | 'y' | 'z'], s = dir[0] === '-' ? -1 : 1;
    const mover = moverPart(object, frame);
    if (!mover) return fail('it has no visible geometry');

    // The part's siblings are parts of their own, even where they and it are bare primitives (one part to partsIn).
    const roots = request.on?.length ? [...request.on] : [scopeOf(object), ...frame.getChildren()];
    const skip = new Set<SceneObject>([object, ...pending, ...(request.ignore ?? [])]);
    const self = new Set([object]);
    let page = Infinity;
    for (const [p, k] of first) if (k < page && within(p, self)) page = k;
    const around = new Map<SceneObject, FitPart>(), others = new Map<SceneObject, FitPart>();
    for (const [owner, pose] of partsIn(frame, roots)) {
        if (within(owner, skip) || within(object, new Set([owner])) || cues.has(owner)) continue;
        around.set(owner, pose.part);
        if ((first.get(owner) ?? -1) <= page) others.set(owner, pose.part);
    }
    const sweep = sweepContact(mover, others, vectorOf(dir), grid);
    // Backing out of a part that holds it would push it out past that part's end.
    if (!checked && sweep.held.length) {
        const names = sweep.held.slice(0, 3).map(o => `"${o.name}"`).join(', ');
        return fail(`it starts inside ${names}, which ${sweep.held.length === 1 ? 'holds' : 'hold'} it (a tube, a bore or a pocket round it, or a hub it runs through). To drop it inside, seat it with ignore: [${names}]; where it rests at a depth, place it by coordinates`);
    }
    const height = mover.hi[a] - mover.lo[a];
    // Moves this small are typing noise: about 2% of the part, at most a tenth of its height along the travel.
    const tol = Math.min(0.02 * sizeOf(mover.lo, mover.hi), 0.1 * height);
    const { touching, beside } = checked && !sweep.nothing && sweep.t > tol ? holders(mover, around, dir, tol, sweep.t, (o: SceneObject) => (first.get(o) ?? -1) > page) : { touching: [], beside: undefined };
    if (!checked && !sweep.nothing && sweep.t !== 0) {
        const position = object.transform.position;
        if (a === 0) position.x += s * sweep.t; else if (a === 1) position.y += s * sweep.t; else position.z += s * sweep.t;
        object.transform.computeWorldMatrix(true);
        object.captureTransform();
    }
    const lead = s < 0 ? mover.body.lo[a] : mover.body.hi[a];
    const startedInside = Math.abs(sweep.t) > tol ? sweep.startedInside : [];
    const [b, c] = [0, 1, 2].filter(k => k !== a);
    // The other runs through it: their boxes overlap on every axis, and the other is the narrower across the travel
    // on one axis, strictly inside it (a rim, a pin); a part round it (a cabinet round a shelf, even as deep) is not.
    const inBox = (p: FitPart): boolean => [0, 1, 2].every(k => p.lo[k] < mover.hi[k] && p.hi[k] > mover.lo[k])
        && [b, c].some(k => { const tol = 0.01 * (mover.hi[k] - mover.lo[k]); return p.lo[k] > mover.lo[k] + tol && p.hi[k] < mover.hi[k] - tol; });
    return {
        ...blank,
        moved: sweep.t,
        asPlaced: Math.abs(sweep.t) <= tol,
        nothing: sweep.nothing,
        face: lead + (sweep.nothing ? 0 : s * sweep.t),
        contacts: sweep.contacts.map(c => ({ object: c.part, share: sweep.across ? c.area / sweep.across : 0, knobs: c.knobs, tops: c.tops })),
        startedInside,
        pins: startedInside.filter(o => { const p = others.get(o)!; return [b, c].every(k => p.lo[k] >= mover.lo[k] && p.hi[k] <= mover.hi[k]); }),
        sideways: startedInside.filter(o => {
            const p = others.get(o)!, inside = (k: number): boolean => p.lo[k] >= mover.lo[k] && p.hi[k] <= mover.hi[k];
            return inside(a) && [b, c].filter(inside).length === 1;
        }),
        through: checked ? sweep.contacts.map(x => x.part).filter(o => inBox(others.get(o)!)) : [],
        across: checked ? sweep.contacts.map(x => x.part).filter(o => {
            const p = others.get(o)!, e = [0, 1, 2].map(k => p.hi[k] - p.lo[k]), long = e.indexOf(Math.max(...e));
            const cross = e.filter((_, k) => k !== long);
            return long !== a && e[long] >= 3 * Math.max(...cross) && Math.max(...cross) <= 2.5 * Math.min(...cross);
        }) : [],
        height,
        touching,
        beside,
        grid,
    };
}

/**
 * What may hold a measured part with a gap of `gap` under it (hung, clipped on, fitted later, screwed in from the
 * side): what touches it now on any side, `later` when its deck shows that part on a later page; touching nothing,
 * the nearest part on another side than `dir`, nearer than `gap` and than half its size, which may hold it by a
 * pin, a screw or a clip.
 */
function holders(mover: FitPart, around: ReadonlyMap<SceneObject, FitPart>, dir: SeatDirection, tol: number, gap: number, later: (o: SceneObject) => boolean): Pick<SeatFact, 'touching' | 'beside'> {
    const touching = new Map<SceneObject, boolean>();
    let beside: SeatFact['beside'];
    for (const d of SEAT_DIRECTIONS) {
        const near = sweepContact(mover, around, vectorOf(d));
        if (near.nothing || !near.contacts.length) continue;
        if (Math.abs(near.t) <= tol) for (const c of near.contacts) touching.set(c.part, later(c.part));
        else if (d !== dir && near.t > 0 && near.t < (beside?.gap ?? Math.min(gap, 0.5 * sizeOf(mover.lo, mover.hi)))) beside = { object: near.contacts[0].part, gap: near.t, dir: d };
    }
    return { touching: [...touching].map(([object, l]) => ({ object, later: l })), beside: touching.size ? undefined : beside };
}

const mm = (v: number): string => (Math.abs(v) < 5e-5 ? '0' : (v * 1000).toFixed(1));
const LIMIT = 40;

function faceName(dir: SeatDirection): string {
    if (dir[1] === 'y') return dir[0] === '-' ? 'bottom' : 'top';
    return `${dir[0] === '-' ? '-' : '+'}${dir[1]} face`;
}

/** The facts as a tool's text: a header, then one line per part, the ones to look at first. */
export function seatText(facts: readonly SeatFact[]): string {
    const look: string[] = [], fine: string[] = [];
    let placed = 0, moved = 0;
    for (const f of facts) {
        const name = f.object.name;
        if (f.error) { look.push(`! ${name}: not seated: ${f.error}.`); continue; }
        const along = f.dir[1] === 'y' ? (f.dir[0] === '-' ? 'down' : 'up') : `along ${f.dir}`;
        if (f.nothing) {
            look.push(f.checked
                ? `! ${name} (not seated): nothing under it along ${f.dir}; fine for a base or a part held from the side.`
                : `! ${name}: nothing in its way ${along} (${f.dir}): left where you put it; it floats unless something holds it from the side.`);
            continue;
        }
        // Knobs are studs only in a model of studded bricks; elsewhere (pins, claws, shanks) they go unnamed.
        const touching = f.contacts.map(c => `${c.object.name} (${f.grid && c.knobs ? `${c.knobs} stud${c.knobs === 1 ? '' : 's'}, ` : c.tops ? 'on stud tops, ' : ''}${Math.round(c.share * 100)}%)`);
        const where = `${faceName(f.dir)} ${f.checked ? 'would be' : 'now'} at ${f.dir[1]} ${mm(f.face)} mm, ${f.dir === '-y' ? 'on' : 'against'} ${touching.join(', ') || '?'}`;
        if (f.checked) {
            // Measured only: a gap is worth a line; an overlap is check_overlaps' to judge.
            if (f.asPlaced || f.moved <= 0) continue;
            const held = (f.touching ?? []).slice(0, 3).map(t => `${t.object.name}${t.later ? ' (a later page)' : ''}`);
            const fix = f.through.length
                ? `; ${f.through[0].name} passes through it (a rim in a tyre, a pin in a hole): if they should touch, make ${f.through[0].name} fill its hole; don't seat it`
                : held.length
                ? `, but it touches ${held.join(', ')}: fine if ${held.length === 1 ? 'that holds' : 'they hold'} it (hung, clipped, fitted later from below); else seat it`
                : f.beside
                ? `; ${f.beside.object.name} is ${mm(f.beside.gap)} mm away along ${f.beside.dir}: if it holds this part from the side (a pin, axle or screw in its hole, a clip), place it there by the hole's coordinates; else seat it`
                : f.across.length && f.across.length === f.contacts.length
                ? `; it would come down onto ${f.across.slice(0, 2).map(o => o.name).join(', ')}, across its travel (a pin or axle): if that goes through its hole, place it there by the hole's coordinates; else seat it`
                : '; seat it';
            look.push(`! ${name} (not seated): ${mm(f.moved)} mm clear of what is ${f.dir === '-y' ? 'under' : 'ahead of'} it${fix}. Seated, its ${where}.`);
            continue;
        }
        const warn: string[] = [];
        if (f.startedInside.length) warn.push(`it started inside ${f.startedInside.slice(0, 3).map(o => o.name).join(', ')}`
            + (f.sideways.length ? `; ${f.sideways[0].name} runs through it sideways (a pin or axle in its hole): place it by the hole's coordinates instead of seating it`
                : f.pins.length ? `; if ${f.pins[0].name} goes inside it (a pin in its socket), seat it with on: [the part it rests against]` : ''));
        if (f.moved < 0 && -f.moved > f.height) warn.push(`it backed out more than its own height (${mm(f.height)} mm): start it on the far side of its rest, clear of other parts`);
        // Support is what it rests on: a part pushed up into place (a bolt from below) is held by its fit, not its face.
        const studs = f.contacts.reduce((n, c) => n + c.knobs, 0);
        if (f.contacts.some(c => c.tops)) warn.push('it stopped on stud tops: if they go inside it, move it their height further');
        else if (f.dir === '-y' && f.contacts.every(c => c.share < 0.25 && !c.knobs)) warn.push(`little support: under 25% of it touches${f.grid ? ', no studs' : ''}`);
        // Typed where it rests: say whether its studs are in, which the contacts' counts say for a part that moved.
        const move = f.asPlaced
            ? `as placed${f.grid && studs ? ', studs engaged' : f.contacts.some(c => c.tops) ? ', on stud tops' : ''}`
            : `${f.moved < 0 ? 'backed out' : 'slid'} ${mm(Math.abs(f.moved))} mm ${f.moved < 0 ? 'against' : 'along'} ${f.dir}`;
        const line = `${name}${f.page === undefined ? '' : ` (on page ${f.page + 1}, the last that shows it)`}: ${move}; ${where}`;
        if (f.asPlaced) placed++; else moved++;
        if (warn.length) look.push(`! ${line} -- ${warn.join('; ')}.`);
        else fine.push(`  ${line}.`);
    }
    const seated = facts.filter(f => !f.checked).length;
    const head = `seat: ${seated} part${seated === 1 ? '' : 's'}: ${placed} as placed, ${moved} moved, ${look.length} to look at. `
        + 'mm in each part\'s parent frame; % = share of its face toward the travel that touches. Parts a deck shows on a later page are not in the way.';
    const body = [...look, ...fine];
    return [head, ...body.slice(0, LIMIT), ...(body.length > LIMIT ? [`  ... ${body.length - LIMIT} more lines`] : [])].join('\n');
}
